<template>
  <div id="app">
    <notifications group="foo" position="top right" />
    <AppConfirmDialog />
    <AppNotification/>
    <div class="app-holder">
      <router-view :key="$route.fullPath"></router-view>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import AppConfirmDialog from '@/components/ui/AppConfirmDialog.vue'
import AppNotification from '@/app/notify/AppNotification.vue'
import jwtService from '@/app/services/jwt.service'
import { refreshSessionToken } from '@/app/services/auth.service'
import { useLoginStore } from '@/stores/login'
import {
  DEFAULT_REFRESH_POLL_MS,
  getRefreshDelayMs,
  MAX_SETTIMEOUT_MS,
  trackStoredSession,
} from '@/composables/tokenRefresh'

const loginStore = useLoginStore()
const { getUserProfileData } = storeToRefs(loginStore)

const MIN_REFRESH_GAP_MS = 15 * 1000
let profileRefreshTimer: ReturnType<typeof setTimeout> | null = null
let refreshInFlight = false
let lastRefreshAt = 0
let retryNotBefore = 0

const isSessionActive = computed(() => {
  trackStoredSession()
  const profile = getUserProfileData.value || jwtService.getUser()
  const token = jwtService.getToken()
  return !!(profile && token)
})

function clearProfilePoll() {
  if (profileRefreshTimer != null) {
    clearTimeout(profileRefreshTimer)
    profileRefreshTimer = null
  }
}

function scheduleProfileRefresh() {
  clearProfilePoll()
  if (!isSessionActive.value) return

  let delay = getRefreshDelayMs()
  const retryIn = retryNotBefore - Date.now()
  if (retryIn > delay) delay = retryIn

  if (lastRefreshAt) {
    const sinceRefresh = Date.now() - lastRefreshAt
    const earliest = MIN_REFRESH_GAP_MS - sinceRefresh
    if (earliest > delay) delay = earliest
  }

  delay = Math.min(delay, MAX_SETTIMEOUT_MS)

  profileRefreshTimer = setTimeout(() => {
    profileRefreshTimer = null
    void runProfileRefresh()
  }, delay)
}

async function runProfileRefresh() {
  if (refreshInFlight) return
  if (!isSessionActive.value) {
    clearProfilePoll()
    return
  }

  const refreshToken = sessionStorage.getItem('tokenRefresh')
  if (!refreshToken) {
    clearProfilePoll()
    return
  }

  refreshInFlight = true
  lastRefreshAt = Date.now()
  let shouldReschedule = false
  try {
    await refreshSessionToken(refreshToken)
    retryNotBefore = 0
    shouldReschedule = true
  } catch (error) {
    console.error('Token refresh failed:', error)
    const status = (error as { response?: { status?: number } })?.response?.status
    if (status !== 401 && status !== 403 && isSessionActive.value) {
      retryNotBefore = Date.now() + DEFAULT_REFRESH_POLL_MS
      shouldReschedule = true
    }
  } finally {
    refreshInFlight = false
  }

  if (shouldReschedule) scheduleProfileRefresh()
}

function onVisibilityChange() {
  if (document.visibilityState !== 'visible' || refreshInFlight) return
  if (!isSessionActive.value || getRefreshDelayMs() > 0 || profileRefreshTimer != null) return
  if (lastRefreshAt && Date.now() - lastRefreshAt < MIN_REFRESH_GAP_MS) return
  void runProfileRefresh()
}

watch(
  isSessionActive,
  (isActive) => {
    if (!isActive) {
      clearProfilePoll()
      return
    }
    scheduleProfileRefresh()
  },
  { immediate: true },
)

onMounted(() => {
  document.addEventListener('visibilitychange', onVisibilityChange)
})

onBeforeUnmount(() => {
  clearProfilePoll()
  document.removeEventListener('visibilitychange', onVisibilityChange)
})
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  overflow-x: hidden;
  height: 100%;
}

html,
body {
  height: 100%;
}

.app-holder {
  height: 100%;
}



*{
  scrollbar-width: thin;
  scrollbar-color: #cdcdcd #f0f0f0;
}

/* Works on Chrome, Edge, and Safari */
*::-webkit-scrollbar {
  width: 8px;
}

*::-webkit-scrollbar-track {
  background: #f0f0f0;
}

*::-webkit-scrollbar-thumb {
  background-color: #cdcdcd;
  /*background: linear-gradient(to top, #33ccff 0%, #cc00ff 100%)*/
}


.noFocus:focus {
  outline: none 0 !important;
  box-shadow: none;
  -moz-box-shadow: none;
  -webkit-box-shadow: none;
}

.noFocus {
  box-shadow: none !important
}

</style>
